/** Доли колонки: у формы Front — числа, у узла дизайнера — строки свойств. */
export interface IGridSpanItem {
    span?: number | string | null;
    spanSm?: number | string | null;
}

const positiveInt = (value: number | string | null | undefined): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
    return Math.floor(parsed);
};

/** Число колонок сетки (`gridColumns` ряда, `columns` узла дизайнера): по умолчанию 12, не больше 24. */
export const gridTrackCount = (value: number | string | null | undefined): number => {
    const parsed = positiveInt(value);
    if (parsed === undefined) return 12;
    return Math.min(24, parsed);
};

/** Зазор ряда: `sm` — 8px, `lg` — 24px, иначе (`md`, пусто) — 16px. */
export const gridGap = (gap: string | null | undefined): string => {
    if (gap === 'sm') return '8px';
    if (gap === 'lg') return '24px';
    return '16px';
};

/**
 * Доли колонок сетки. null — колонку не рисовать.
 * Без явного span оставшиеся доли делятся поровну.
 * collapseHidden=grow отдаёт доли скрытых колонок последней видимой,
 * чтобы ряд не заканчивался пустым хвостом. Порядок элементов не меняется.
 */
export const resolveGridSpans = (
    items: IGridSpanItem[],
    tracksInput: number | string | null | undefined,
    hidden: boolean[],
    grow: boolean,
    useSm = false,
): Array<number | null> => {
    const tracks = gridTrackCount(tracksInput);
    const requested = items.map((item) => positiveInt(useSm ? (item.spanSm ?? item.span) : item.span));
    const specified = requested.filter((span): span is number => span !== undefined);
    const used = specified.reduce((sum, span) => sum + span, 0);
    const unspecified = requested.length - specified.length;
    const rest = Math.max(0, tracks - used);
    const share = unspecified > 0 ? Math.max(1, Math.floor(rest / unspecified)) : 1;
    const spans = requested.map((span) => Math.min(tracks, Math.max(1, span ?? share)));
    if (unspecified > 0) {
        let leftover = rest - share * unspecified;
        for (let index = spans.length - 1; index >= 0 && leftover > 0; index -= 1) {
            if (requested[index] === undefined) {
                spans[index] += 1;
                leftover -= 1;
            }
        }
    }

    const hiddenSpan = spans.reduce((sum, span, index) => sum + (hidden[index] ? span : 0), 0);
    let lastVisible = -1;
    spans.forEach((_, index) => {
        if (!hidden[index]) lastVisible = index;
    });

    return spans.map((span, index) => {
        if (hidden[index]) return null;
        if (grow && index === lastVisible) return Math.min(tracks, span + hiddenSpan);
        return span;
    });
};
